'use client';

import { useEffect, useRef, useState } from 'react';

import { updateLegacyUrls } from '@core/Utils/Strings/UpdateLegacyUrls';
import { IContentItemElements } from '@kontent-ai/delivery-sdk';
import { FaqSection } from '@page-builder/Components/FaqSection/FaqSection';
import throttle from 'lodash.throttle';

import ContentsLinks from '../ContentsLinks/ContentsLinks';
import ReviewSection from '../ReviewSection/ReviewSection';

interface ReviewBodyProps {
  reviewContent: IContentItemElements;
  strings: Record<string, string>;
}

const ReviewBody = ({ reviewContent, strings }: ReviewBodyProps) => {
  const [activeSection, setActiveSection] = useState<string | undefined>(undefined);
  const {
    overall_verdict,
    on_the_inside,
    driving,
    how_much_does_it_cost_to_run,
    prices_versions_and_specification,
    common_questions,
  } = reviewContent;
  const contentsLinksRef = useRef<HTMLDivElement>(null);
  const refs: any = {
    commonQuestions: useRef<HTMLElement>(null),
    costToRun: useRef<HTMLElement>(null),
    driving: useRef<HTMLElement>(null),
    onTheInside: useRef<HTMLElement>(null),
    overallVerdict: useRef<HTMLElement>(null),
    pricesAndSpecs: useRef<HTMLElement>(null),
  };

  const sections = [
    {
      id: 'overall',
      contentObj: overall_verdict,
      ref: refs.overallVerdict,
    },
    {
      id: 'inside',
      contentObj: on_the_inside,
      ref: refs.onTheInside,
    },
    {
      id: 'driving',
      contentObj: driving,
      ref: refs.driving,
    },
    {
      id: 'running-cost',
      contentObj: how_much_does_it_cost_to_run,
      ref: refs.costToRun,
    },
    {
      id: 'versions',
      contentObj: prices_versions_and_specification,
      ref: refs.pricesAndSpecs,
    },
  ];

  const commonQs = common_questions.map(question => {
    return {
      accordionTitle: question.elements.question.value,
      accordionContent: updateLegacyUrls(question.elements.answer.value),
      byline: question.elements.author?.value?.[0],
    };
  });
  const hasCommonQs = commonQs.length > 0;

  if (hasCommonQs) {
    sections.push({
      id: 'questions',
      contentObj: common_questions,
      ref: refs.commonQuestions,
    });
  }

  const handleScroll = throttle(() => {
    if (contentsLinksRef.current) {
      const navigationInDom = contentsLinksRef.current!.getBoundingClientRect();
      sections.forEach(secData => {
        const sectionInDom = secData.ref!.current.getBoundingClientRect();
        const hasNavPassedSectionTop = navigationInDom.bottom >= sectionInDom.top;
        const hasNavPassedSectionBottom = navigationInDom.bottom >= sectionInDom.bottom;
        if (hasNavPassedSectionTop && !hasNavPassedSectionBottom) {
          setActiveSection(secData.id);
        }
      });
    }
  }, 100);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <>
      <ContentsLinks
        strings={strings}
        hasCommonQuestions={hasCommonQs}
        activeSectionId={activeSection}
        ref={contentsLinksRef}
      />

      {sections.map(secData => {
        return (
          <ReviewSection
            key={secData.id}
            id={secData.id}
            sectionData={secData.contentObj}
            ref={secData.ref}
          />
        );
      })}

      {hasCommonQs && (
        <FaqSection
          id="questions"
          questionAnswerPairs={commonQs}
          title={strings.askTheExperts}
          useHTagsInAccordion
        />
      )}
    </>
  );
};

export default ReviewBody;
